import { useTranslation } from 'react-i18next';
import SCUIGenericExcelUpload from '../../../../atomic/molecules/SCUIGenericExcelUpload/SCUIGenericExcelUpload';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import Swal from 'sweetalert2';
import { useState } from 'react';
import ShippingCommand from '../../../../services/command/shipping/ShippingCommand.services';
import { LCWFEE } from '../../lcwCargoFeeManagement.classes';

export const BulkExcelUploadTab = ({
    setIsLoading,
    isLoading,
    companyOptions,
}: {
    setIsLoading: (isLoading: boolean) => void;
    isLoading: boolean;
    companyOptions: any[];
}) => {
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [uploadResponse, setUploadResponse] = useState<any>(null);

    const { t } = useTranslation();

    const exampleData = [
        {
            desi: 0,
            cost: 15.5,
            startDate: '15-01-2024',
            endDate: '31-12-2024',
        },
        {
            desi: 1,
            cost: 19.75,
            startDate: '10-01-2024',
            endDate: '31-12-2024',
        },
    ];

    const onUpload = async (file: File) => {
        setIsLoading(true);
        try {
            setUploadResponse(null);
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('ShippingCompanyId', selectedCompanyId);
            const response = await ShippingCommand.UpsertBulkShippingCompanyCostForDesi(formData);
            if (response) {
                setUploadResponse(response);

                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text:
                        t('fastDeliveryCargoManagement.bulkUploadSuccess') +
                        ', ' +
                        t('fastDeliveryCargoManagement.mismatchedDataWillBeShown'),
                    showConfirmButton: false,
                    timer: 2500,
                });
            }
        } catch (error: any) {
            setUploadResponse(null);

            // Check if error response has the specific structure with Key field
            let errorMessage = t('fastDeliveryCargoManagement.bulkUploadError');

            if (error?.data?.Key) {
                errorMessage = error.data.Key;
            }

            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: errorMessage,
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeCompany = (event: SelectChangeEvent) => {
        setSelectedCompanyId(event.target.value);
    };

    // Render status counts
    const renderStatusCounts = () => {
        if (!uploadResponse?.itemStatusCountInfos) return null;

        const { totalRecords, updatedRecords, insertedRecords, failedRecords } = uploadResponse.itemStatusCountInfos;

        return (
            <Box className={LCWFEE.bulkStatusChipsWrap} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2, marginBottom: 2 }}>
                <Chip
                    className={LCWFEE.chipBulkTotal}
                    label={`${t('lcwCargoFeeManagement.total')}: ${totalRecords}`}
                    sx={{ backgroundColor: '#2196f3', color: 'white' }}
                />
                <Chip
                    className={LCWFEE.chipBulkUpdated}
                    label={`${t('lcwCargoFeeManagement.updated')}: ${updatedRecords}`}
                    sx={{ backgroundColor: '#ff9800', color: 'white' }}
                />
                <Chip
                    className={LCWFEE.chipBulkInserted}
                    label={`${t('lcwCargoFeeManagement.inserted')}: ${insertedRecords}`}
                    sx={{ backgroundColor: '#4caf50', color: 'white' }}
                />
                <Chip
                    className={LCWFEE.chipBulkFailed}
                    label={`${t('lcwCargoFeeManagement.failed')}: ${failedRecords}`}
                    sx={{ backgroundColor: '#f44336', color: 'white' }}
                />
            </Box>
        );
    };

    // Render failed items table
    const renderFailedItemsTable = () => {
        if (!uploadResponse?.failedItems || uploadResponse.failedItems?.length === 0) return null;

        // Flatten failed items with their error messages
        const failedRows = uploadResponse.failedItems.flatMap((item: any) =>
            item.errorModelList.map((error: any, errorIndex: number) => ({
                rowNumber: item.index,
                columnName: error.fieldName,
                message: error.message,
                uniqueKey: `${item.index}-${errorIndex}`,
            }))
        );

        return (
            <Box className={LCWFEE.bulkFailedSection} sx={{ marginTop: 3 }}>
                <p className={LCWFEE.bulkFailedTitle} style={{ marginBottom: 2, color: '#f44336', fontWeight: 'bold', fontSize: '16px' }}>
                    {t('lcwCargoFeeManagement.failedRecords')}
                </p>
                <TableContainer className={LCWFEE.bulkFailedTablePaper} component={Paper}>
                    <Table className={LCWFEE.bulkFailedTable}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('lcwCargoFeeManagement.rowNumber')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('lcwCargoFeeManagement.columnName')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('lcwCargoFeeManagement.message')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {failedRows.map((row: any) => (
                                <TableRow key={row.uniqueKey}>
                                    <TableCell>{row.rowNumber}</TableCell>
                                    <TableCell>{row.columnName}</TableCell>
                                    <TableCell>{row.message}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Box className={LCWFEE.bulkRoot} width="100%">
            <p className={LCWFEE.bulkTitle} style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {t('fastDeliveryCargoManagement.bulkUpload')}
            </p>
            <p className={LCWFEE.bulkSubtitle} style={{ fontSize: '12px', marginTop: '8px' }}>
                {t('lcwCargoFeeManagement.bulkUploadText')}
            </p>

            <FormControl className={LCWFEE.bulkCompanyWrap} fullWidth sx={{ width: '40%', marginBottom: '16px', marginTop: '16px' }}>
                <InputLabel id="filter-company-label">
                    {t('lcwCargoFeeManagement.list.columns.cargoCompany')}
                </InputLabel>
                <Select
                    className={LCWFEE.inputSelectBulkCompany}
                    labelId="filter-company-label"
                    label={t('lcwCargoFeeManagement.list.columns.cargoCompany')}
                    value={selectedCompanyId}
                    onChange={handleChangeCompany}
                >
                    {(companyOptions || []).map(
                        (opt) =>
                            opt.id !== -1 && (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {opt.name}
                                </MenuItem>
                            )
                    )}
                </Select>
            </FormControl>
            <SCUIGenericExcelUpload
                className={LCWFEE.bulkUploadSection}
                exampleWrapClassName={LCWFEE.bulkUploadExampleWrap}
                dropzoneClassName={LCWFEE.bulkUploadDropzone}
                fileInputClassName={LCWFEE.inputFileBulkExcel}
                selectFileButtonClassName={LCWFEE.inputButtonSelectBulkExcel}
                uploadButtonClassName={LCWFEE.inputButtonUploadBulkExcel}
                onUpload={onUpload}
                uploadButtonText={t('fastDeliveryCargoManagement.upload')}
                selectFileButtonText={t('fastDeliveryCargoManagement.selectExcelFile')}
                showExample={true}
                exampleData={exampleData}
                exampleButtonText={t('general.exampleExcel')}
                loading={isLoading}
                style={{ width: '100%' }}
                disabled={!selectedCompanyId}
                exampleFileName={t('lcwCargoFeeManagement.exampleFileName')}
            />

            {/* Display status counts */}
            {renderStatusCounts()}

            {/* Display failed items table */}
            {renderFailedItemsTable()}
        </Box>
    );
};

export default BulkExcelUploadTab;
